import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGenerateSubscriptionFormMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { notificationCreated } from 'reducers/appNotification';
import { downloadClick, deviceIsMobile } from 'utils/helpers';
import { useSubscriptionDocument, useTunnelChangeHandlers } from 'utils/hooks';
import { stepStatus, docTypes, subscriptionDocStatus } from 'utils/constants';
import { documentApi } from 'api/main';
import { texts } from 'utils/languages/fr';

import { Loader, CustomButton, SubmitButton } from 'components';
import { Box, Typography } from '@mui/material';
import MandatoryFormDialog from './MandatoryFormDialog';

const SubscriptionFormPreview = props => {
    const { userStep, handleToggle } = props;
    const mounted = useRef(false);
    const dispatch = useDispatch();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const [generateSubscriptionForm, { isLoading: generateLoader }] =
        useGenerateSubscriptionFormMutation();

    const subscriptionFormStatus = useSelector(userSelectors.getSubscriptionFormStatus);
    const internalUser = useSelector(userSelectors.getInternalUser);

    const [subscriptionFormURL, setSubscriptionFormURL] = useState('');
    const [docDownloading, setDocDownloading] = useState(false);
    const [disclaimerDialogOpen, setDisclaimerDialogOpen] = useState(false);
    const subscriptionForm = useSubscriptionDocument(docTypes.SUBSCRIPTION_FORM);
    const docIsBeingGenerated = subscriptionFormStatus === subscriptionDocStatus.GENERATING;
    const finishedGenerating = subscriptionFormStatus === subscriptionDocStatus.GENERATED;
    const isImpersonated = !!internalUser?.impersonatedByUser;
    const stepIncompleteOrFailed = useMemo(
        () =>
            userStep?.status === stepStatus.INCOMPLETE ||
            userStep?.status === stepStatus.FAILED ||
            !userStep,
        [userStep]
    );

    const handleDownload = useCallback(() => {
        if (!subscriptionFormURL) return;
        downloadClick(subscriptionFormURL, subscriptionForm.name);
    }, [subscriptionFormURL, subscriptionForm]);

    const handleOpenDocument = useCallback(() => {
        if (!subscriptionFormURL) return;
        const tab = window.open();
        tab.location.href = subscriptionFormURL;
    }, [subscriptionFormURL]);

    const getDocument = useCallback(() => {
        setDocDownloading(true);
        documentApi
            .downloadDocumentById(subscriptionForm.id)
            .then(response => {
                if (!mounted.current) return;
                const bytes = new Uint8Array(response.data);
                let file = new File([bytes], subscriptionForm.name, {
                    type: 'application/pdf',
                });
                const url = window.URL.createObjectURL(file);
                setSubscriptionFormURL(url);
                setDocDownloading(false);
            })
            .catch(error => {
                if (!mounted.current) return;
                // ignore the NOT_FOUND errors caused by the old document being deleted
                if (error?.status !== 404) {
                    dispatch(notificationCreated('FAILED_DOCUMENT_DOWNLOAD'));
                }
                setDocDownloading(false);
            });
    }, [dispatch, subscriptionForm]);

    const handleOpenDialog = useCallback(() => setDisclaimerDialogOpen(true), []);
    const handleCloseDialog = useCallback(() => setDisclaimerDialogOpen(false), []);
    const handleContinue = useCallback(() => {
        handleCloseDialog();
        handleToggle();
    }, [handleCloseDialog, handleToggle]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (
            !docIsBeingGenerated &&
            (!subscriptionForm || (stepIncompleteOrFailed && !finishedGenerating))
        ) {
            generateSubscriptionForm();
        } else if (!!subscriptionForm && !docIsBeingGenerated) {
            getDocument();
        }
    }, [
        subscriptionForm,
        getDocument,
        generateSubscriptionForm,
        stepIncompleteOrFailed,
        docIsBeingGenerated,
        finishedGenerating,
    ]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => true,
                docDownloading
            ),
        [docDownloading, setTunnelChangesHandlers]
    );

    return (
        <Loader loading={generateLoader || docDownloading} size={40}>
            {docIsBeingGenerated ? (
                <React.Fragment>
                    <Typography align="center" variant="subtitle1">
                        {texts.BULLETIN_GENERATING}
                    </Typography>
                    <Typography align="center" variant="subtitle1">
                        <Typography display="inline">{texts.PLEASE_WAIT}</Typography>
                        <Typography fontWeight="600" display="inline">
                            {texts.WAIT_TO_SIGN}
                        </Typography>
                    </Typography>
                </React.Fragment>
            ) : (
                !docDownloading &&
                !!subscriptionForm && (
                    <React.Fragment>
                        <Typography align="center" variant="subtitle1">
                            {texts.PLEASE_SIGN}
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-around"
                            flexWrap="wrap"
                            height="150px"
                            paddingX={12}
                        >
                            {!deviceIsMobile() && (
                                <CustomButton
                                    sx={{ borderRadius: '20px' }}
                                    size="small"
                                    onClick={handleOpenDocument}
                                    label={texts.CONSULTER}
                                    variant="contained"
                                />
                            )}
                            <CustomButton
                                sx={{ borderRadius: '20px' }}
                                size="small"
                                onClick={handleDownload}
                                label={texts.DOWNLOAD}
                                variant="contained"
                            />
                        </Box>
                    </React.Fragment>
                )
            )}
            <MandatoryFormDialog
                open={disclaimerDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleContinue}
            />
            {!isImpersonated && !generateLoader && !docDownloading && !docIsBeingGenerated && (
                <SubmitButton text={texts.SIGN} onClick={handleOpenDialog} />
            )}
        </Loader>
    );
};

export default SubscriptionFormPreview;
